/*
  BIGM - Collection landing behaviour
  ------------------------------------------------------------------
  Progressive enhancement only: every section renders and works without it.

  1. Read more toggle for the hero intro (clamped to 2 lines on mobile).
  2. Overflow cue on the mobile pathway chips (fade removed at the end).
  3. "Showing N of X" progress beside Minimog's Load more button.
  4. Back-button restoration: returning from a product page reloads the
     same number of products and scrolls to the card that was opened.
     Minimog's own Load more does not touch history, so without this a
     shopper who opened product 40 lands back on the first 24.
  5. Light dataLayer events for pathways and Load more (read by GTM/Stape).

  Relies on Minimog markup: #CollectionProductGrid, [data-product-container],
  .m-product-item, [data-product-id] and [data-load-more]. If a Minimog
  update renames those, 3 and 4 silently switch off; nothing breaks.
*/
(() => {
  'use strict';

  const dl = (payload) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  };

  /* ---------- 1. Read more ---------- */
  const initReadMore = () => {
    document.querySelectorAll('[data-bigm-clamp]').forEach((text) => {
      const btn = text.parentElement.querySelector('[data-bigm-more]');
      if (!btn) return;
      const overflowing = () => text.scrollHeight - text.clientHeight > 2;
      const sync = () => {
        btn.hidden = !text.classList.contains('is-expanded') && !overflowing();
      };
      btn.addEventListener('click', () => {
        const open = text.classList.toggle('is-expanded');
        btn.setAttribute('aria-expanded', String(open));
        btn.textContent = open ? btn.dataset.lessLabel : btn.dataset.moreLabel;
        sync();
      });
      sync();
      // Web fonts and viewport changes alter whether the text overflows.
      if ('ResizeObserver' in window) {
        new ResizeObserver(sync).observe(text);
      } else {
        window.addEventListener('resize', sync, { passive: true });
      }
    });
  };

  /* ---------- 2. Pathway overflow cue + tracking ---------- */
  const initPathways = () => {
    document.querySelectorAll('[data-bigm-pathways]').forEach((list) => {
      const sync = () => {
        const end = list.scrollLeft + list.clientWidth >= list.scrollWidth - 4;
        list.classList.toggle('is-end', end);
      };
      list.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync, { passive: true });
      sync();

      list.addEventListener('click', (e) => {
        const link = e.target.closest('[data-bigm-pathway]');
        if (!link) return;
        dl({
          event: 'bigm_pathway_click',
          bigm_collection: list.dataset.collection,
          bigm_pathway: link.dataset.bigmPathway,
          bigm_position: Number(link.dataset.position) || 0,
        });
      });
    });
  };

  /* ---------- 3 + 4. Progress and Back restoration ---------- */
  const shopBar = document.querySelector('[data-bigm-shop]');
  const getContainer = () =>
    document.querySelector('#CollectionProductGrid [data-product-container]');
  const countItems = (c) => c.querySelectorAll('.m-product-item').length;
  const storeKey = () => 'bigm-clp:' + location.pathname + location.search;
  const MAX_AGE = 30 * 60 * 1000;

  const readState = () => {
    try {
      const s = JSON.parse(sessionStorage.getItem(storeKey()) || 'null');
      return s && Date.now() - s.t < MAX_AGE ? s : null;
    } catch (e) {
      return null;
    }
  };

  const writeState = (s) => {
    try {
      sessionStorage.setItem(storeKey(), JSON.stringify(s));
    } catch (e) {
      /* private mode or storage full: restoration just does not happen */
    }
  };

  // Minimog's sort and filter AJAX replaces the whole grid section, so the
  // progress element is re-attached to whichever container exists right now.
  const renderProgress = (total) => {
    const container = getContainer();
    let wrap = document.querySelector('.bigm-clp-progress');
    if (!container || total <= 0) {
      if (wrap) wrap.hidden = true;
      return;
    }
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'bigm-clp-progress';
      wrap.innerHTML =
        '<p class="bigm-clp-progress__text" aria-live="polite"></p>' +
        '<div class="bigm-clp-progress__bar" aria-hidden="true"><span class="bigm-clp-progress__fill"></span></div>';
    }
    if (container.nextElementSibling !== wrap) container.insertAdjacentElement('afterend', wrap);
    const label = (shopBar && shopBar.dataset.progressLabel) || 'Showing [shown] of [total]';
    const shown = Math.min(countItems(container), total);
    const text = label.replace('[shown]', shown).replace('[total]', total);
    const p = wrap.querySelector('.bigm-clp-progress__text');
    if (p.textContent !== text) p.textContent = text;
    wrap.querySelector('.bigm-clp-progress__fill').style.width = Math.round((shown / total) * 100) + '%';
    wrap.hidden = false;
  };

  const waitForMore = (container, before, timeout = 8000) =>
    new Promise((resolve) => {
      if (countItems(container) > before) return resolve(true);
      const obs = new MutationObserver(() => {
        if (countItems(container) > before) {
          obs.disconnect();
          clearTimeout(timer);
          resolve(true);
        }
      });
      const timer = setTimeout(() => {
        obs.disconnect();
        resolve(false);
      }, timeout);
      obs.observe(container, { childList: true });
    });

  const restore = async (get, state) => {
    let guard = 0;
    let container = get();
    while (container && countItems(container) < state.count && guard < 20) {
      const btn = document.querySelector('#CollectionProductGrid [data-load-more]');
      if (!btn) break;
      const before = countItems(container);
      btn.click();
      const ok = await waitForMore(container, before);
      if (!ok) break;
      guard += 1;
      container = get();
    }
    if (!container) return;
    const card = state.id
      ? container.querySelector('[data-product-id="' + CSS.escape(String(state.id)) + '"]')
      : null;
    const item = card && card.closest('.m-product-item');
    if (item) {
      item.scrollIntoView({ block: 'center' });
      const link = item.querySelector('a[href*="/products/"]');
      if (link) link.focus({ preventScroll: true });
    }
  };

  const initGrid = () => {
    const container = getContainer();
    if (!container) return;
    const total = shopBar ? Number(shopBar.dataset.total) || 0 : 0;
    // Note: total is the unfiltered collection count. If filters are switched
    // on, the shop bar count and this total will not follow the filtered set.

    let queued = false;
    const refresh = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        renderProgress(total);
      });
    };
    refresh();
    const scope = container.closest('.shopify-section') || document.body;
    new MutationObserver(refresh).observe(scope, { childList: true, subtree: true });

    document.addEventListener('click', (e) => {
      const current = getContainer();
      if (!current) return;
      const btn = e.target.closest('[data-load-more]');
      if (btn && e.isTrusted && scope.contains(btn)) {
        dl({ event: 'bigm_load_more', bigm_shown: countItems(current), bigm_total: total });
      }
      const link = e.target.closest('a[href*="/products/"]');
      if (!link || !current.contains(link)) return;
      const card = link.closest('[data-product-id]');
      writeState({
        count: countItems(current),
        id: card ? card.dataset.productId : null,
        t: Date.now(),
      });
    });

    const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    const state = readState();
    if (nav && nav.type === 'back_forward' && state && state.count > countItems(container)) {
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      restore(getContainer, state);
    }
  };

  const start = () => {
    initReadMore();
    initPathways();
    // Minimog binds its Load more handler on window load, so wait for it.
    if (document.readyState === 'complete') {
      setTimeout(initGrid, 60);
    } else {
      window.addEventListener('load', () => setTimeout(initGrid, 60), { once: true });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
